import {
    Add,
    ArrowDownward,
    ArrowUpward,
    DeleteOutline,
} from '@mui/icons-material';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import api from '../../api';
import { ColorSwatchLabel } from '../HtmlColorField';
import { filamentLabel } from '../../helpers/filaments';
import { productivityChipLabel } from '../../helpers/printerProductivity';

function emptyRow() {
    return { filament_uuid: '', location: '', filament: null };
}

export default function PrinterFilamentsDialog({
    open,
    onClose,
    printerUuid,
    printerName,
    initialFilaments = [],
    restriction = null,
    onSaved,
}) {
    const [rows, setRows] = useState([]);
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        setRows(
            (initialFilaments || []).map((item) => ({
                filament_uuid: item.filament_uuid,
                location: item.location || '',
                filament: {
                    id: item.filament_uuid,
                    type: item.type,
                    color: item.color,
                    color_label: item.color_label,
                    brand: item.brand,
                },
            })),
        );
        setLoading(true);
        api.get('filaments', { params: { page: 1, limit: 200, archived: '' } })
            .then((response) => {
                setOptions(response.data.materials || []);
            })
            .catch(() => setOptions([]))
            .finally(() => setLoading(false));
        return undefined;
    }, [open, initialFilaments]);

    const selectedIds = useMemo(
        () => new Set(rows.map((row) => row.filament_uuid).filter(Boolean)),
        [rows],
    );

    const moveRow = (index, direction) => {
        setRows((prev) => {
            const next = [...prev];
            const target = index + direction;
            if (target < 0 || target >= next.length) {
                return prev;
            }
            const tmp = next[index];
            next[index] = next[target];
            next[target] = tmp;
            return next;
        });
    };

    const handleSave = async () => {
        for (const row of rows) {
            if (!row.filament_uuid || !String(row.location || '').trim()) {
                await Swal.fire({
                    icon: 'warning',
                    title: 'Preencha Local e Filamento',
                    text: 'Cada linha precisa de um local e um filamento.',
                });
                return;
            }
        }
        setSaving(true);
        try {
            const response = await api.put(`printers/${printerUuid}/filaments`, {
                filaments: rows.map((row) => ({
                    filament_uuid: row.filament_uuid,
                    location: String(row.location).trim(),
                })),
            });
            onSaved?.(response.data.filaments || []);
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Não foi possível salvar',
                text: error?.response?.data?.error || 'Tente novamente.',
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
        >
            <DialogTitle>
                Filamentos · {printerName || 'Impressora'}
            </DialogTitle>
            <DialogContent dividers>
                {restriction ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {productivityChipLabel(restriction)}. Não é possível atribuir filamento enquanto a restrição estiver aberta.
                    </Alert>
                ) : null}
                <Stack spacing={1.5}>
                    {rows.map((row, index) => (
                        <Box
                            key={`row-${index}`}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1.4fr auto' },
                                gap: 1,
                                alignItems: 'center',
                            }}
                        >
                            <TextField
                                size="small"
                                label="Local"
                                placeholder="Slot 1, CFS 3 - Slot 2…"
                                value={row.location}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    setRows((prev) => prev.map((item, i) => (
                                        i === index ? { ...item, location: value } : item
                                    )));
                                }}
                            />
                            <Autocomplete
                                size="small"
                                loading={loading}
                                options={options.filter((opt) => (
                                    opt.id === row.filament_uuid || !selectedIds.has(opt.id)
                                ))}
                                value={row.filament}
                                getOptionLabel={(opt) => filamentLabel(opt)}
                                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                                onChange={(_, value) => {
                                    setRows((prev) => prev.map((item, i) => (
                                        i === index
                                            ? {
                                                ...item,
                                                filament: value,
                                                filament_uuid: value?.id || '',
                                            }
                                            : item
                                    )));
                                }}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.id}>
                                        <ColorSwatchLabel
                                            color={option.color}
                                            label={filamentLabel(option)}
                                        />
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField {...params} label="Filamento" />
                                )}
                            />
                            <Box sx={{ display: 'flex' }}>
                                <IconButton size="small" onClick={() => moveRow(index, -1)} disabled={index === 0}>
                                    <ArrowUpward fontSize="small" />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={() => moveRow(index, 1)}
                                    disabled={index === rows.length - 1}
                                >
                                    <ArrowDownward fontSize="small" />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                                >
                                    <DeleteOutline fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    ))}
                </Stack>
                <Button
                    startIcon={<Add />}
                    sx={{ mt: 2 }}
                    onClick={() => setRows((prev) => [...prev, emptyRow()])}
                    disabled={Boolean(restriction)}
                >
                    Adicionar filamento
                </Button>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancelar</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving || Boolean(restriction)}>
                    Salvar
                </Button>
            </DialogActions>
        </Dialog>
    );
}
